import type { Property } from '@/core/api/models/profile';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { resolveDynatraceUserIdentity, setDynatraceUserEmail } from '@/core/services/dynatraceService';
import { getValue } from '@/core/services/translationService';
import { useAppStore } from '@/core/stores/appStore';
import { mapUnitToProperty } from '@/modules/auth/utils/mapUnitToProperty';
import { shouldInvokeSkipPasscodeApi } from '@/modules/passcode/utils/skipPasscodeGate';
import { useCallback, useEffect, useRef, useState } from 'react';
import { profileService, mapProfileDetailsToUserPatch } from '../services/profileService';

/**
 * Custom hook for profile fetching business logic
 * Handles profile API call, maps units to properties, and updates appStore
 */
export const useProfile = () => {
    const [error, setError] = useState<string | null>(null);

    // Get store state and actions
    const profileLoading = useAppStore((state) => state.profileLoading);
    const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);
    const setProfile = useAppStore((state) => state.setProfile);
    const setProperties = useAppStore((state) => state.setProperties);
    const setProfileLoading = useAppStore((state) => state.setProfileLoading);

    // Get current language for translations
    const { currentLanguage } = useI18nContext();

    // Use ref to store translation function - this allows us to get current translations
    // without including it in dependency arrays (which would cause unnecessary re-renders)
    const getTranslationRef = useRef<(key: string) => string>(() => 'Failed to fetch profile');

    // Update translation function when language changes (but don't include in fetchProfile deps)
    useEffect(() => {
        getTranslationRef.current = (key: string) => {
            try {
                return getValue(currentLanguage, key) || 'Failed to fetch profile';
            } catch {
                return 'Failed to fetch profile';
            }
        };
    }, [currentLanguage]);

    /**
     * Fetch profile details from API and update store
     * CRITICAL: This function must NOT depend on translation hooks to prevent
     * unnecessary API calls when language changes. Only stable dependencies allowed.
     */
    const fetchProfile = useCallback(async (): Promise<void> => {
        // Authentication guard: prevent API calls when logged out
        if (!isAuthenticated) {
            logger.debug('useProfile:Skip fetch - user not authenticated');
            return;
        }

        // ADHA catalogue: profile-details is loaded at login — skip here.
        // Converted leads (UAE Pass / Emirates ID → normal dashboard) must fetch.
        if (useAppStore.getState().isAdhaJourney) {
            logger.debug('useProfile:Skip fetch - ADHA journey (profile loaded at login)');
            return;
        }

        // Email/password only: wait until skip/verify passcode has set passcodeToken.
        // UAE Pass / Emirates ID converted leads have no passcode flow — fetch immediately.
        const authState = useAppStore.getState().auth;
        if (
            shouldInvokeSkipPasscodeApi() &&
            !authState.passcodeToken &&
            authState.token
        ) {
            logger.debug('useProfile:Skip fetch - passcode setup in progress (waiting for passcode token)');
            return;
        }

        try {
            setError(null);
            setProfileLoading(true);

            logger.info('useProfile:Fetching profile details');

            // Call API service
            const response = await profileService.getProfileDetails();

            // Map units to properties
            const properties: Property[] = (response.units || []).map(mapUnitToProperty);

            const profilePatch = mapProfileDetailsToUserPatch(response);

            const currentUser = useAppStore.getState().auth.user;
            const dynatraceUserId = resolveDynatraceUserIdentity({
                ...currentUser,
                ...profilePatch,
            });
            if (dynatraceUserId) {
                await setDynatraceUserEmail(dynatraceUserId);
            }

            setProfile(profilePatch);

            // Update store with properties
            setProperties(properties);

            logger.info('useProfile:Profile fetched successfully', {
                propertiesCount: properties.length,
                hasProfile: !!profilePatch.full_name,
            });

        } catch (err) {
            // Get translated error message using ref (not dependency) to avoid
            // unnecessary re-renders when language changes
            // The ref is updated in useEffect when language changes, so we always get current translation
            const errorMessage = err instanceof Error
                ? err.message
                : getTranslationRef.current('profile.fetchError');
            setError(errorMessage);
            logger.error('useProfile:Failed to fetch profile', err);
            throw err;
        } finally {
            setProfileLoading(false);
        }
    }, [isAuthenticated, setProfile, setProperties, setProfileLoading]);

    /**
     * Retry profile fetch (clears error and calls fetchProfile)
     */
    const retry = useCallback(async (): Promise<void> => {
        setError(null);
        await fetchProfile();
    }, [fetchProfile]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // State
        isLoading: profileLoading,
        error,

        // Actions
        fetchProfile,
        retry,
        clearError,
    };
};

